import { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Plus, RefreshCw, X } from 'lucide-react';
import api from '../lib/api';
import { useAppConfig } from '../lib/appConfig';
import { isEpisodeCard, groupEpisodes } from '../lib/longformGroups';
import { cardTitle } from '../lib/cardTitle';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import Tag from '../ui/Tag';
import Tabs from '../ui/Tabs';
import Select from '../ui/Select';
import Input from '../ui/Input';
import DatePicker from '../ui/DatePicker';
import Popover from '../ui/Popover';
import Menu from '../ui/Menu';
import ListRow from '../ui/ListRow';
import CalendarCell from '../ui/CalendarCell';
import EmptyState from '../ui/EmptyState';
import ErrorState from '../ui/ErrorState';
import Skeleton from '../ui/Skeleton';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { usePageHeader } from '../shell/pageHeader';
import { clean } from '../shell/copy';

// Posting scheduler on src/ui (DESIGN rev 2.1 §3.1: Calendar cell on a month frame, Tabs for Queue / Calendar).
// Same calls as before: GET /api/scheduler, approved live (+ AI when enabled) cards, clients; PATCH a slot's date,
// POST swap, POST add, DELETE remove. Dragging a chip onto a day sets its date, exactly as before.

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const CAL_MAX_CHIPS = 3;

// ── Helpers ───────────────────────────────────────────────────────────────────

function toInputDate(str) {
  if (!str) return '';
  return typeof str === 'string' ? str.slice(0, 10) : new Date(str).toISOString().slice(0, 10);
}

function formatDisplayDate(str) {
  if (!str) return '';
  const d = new Date(str + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const prodOf = slot => slot.liveProduction ?? slot.aiProduction;

/** Chip / peek / picker title for a slot: episode cards read "Series, Ep N" (D-120); others unchanged. */
function slotTitle(slot) {
  const prod = prodOf(slot);
  return clean(isEpisodeCard(prod) ? cardTitle(prod) : (prod?.script?.scriptName ?? `Slot #${slot.slotOrder}`));
}

const episodeLabel = p => `Ep ${p.episodeNumber}${p.episodeTitle ? `: ${clean(p.episodeTitle)}` : ''}`;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const pad = n => String(n).padStart(2, '0');

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Scheduler() {
  const { aiProductionEnabled } = useAppConfig();
  const confirm = useConfirm();
  const toast = useToast();
  const [slots,         setSlots]         = useState([]);
  const [approved,      setApproved]      = useState([]);
  const [clients,       setClients]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [clientFilter,  setClientFilter]  = useState('');
  const [view,          setView]          = useState('queue'); // 'queue' | 'calendar'

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // With AI Production disabled the backend returns no AI slots; skipping
      // the AI available-list fetch here empties aiAvail, so the AI track
      // column never renders (hasAI is derived from slots + available items).
      const [slotsRes, liveRes, aiRes, clientsRes] = await Promise.all([
        api.get('/api/scheduler'),
        api.get('/api/live-production?status=Approved'),
        aiProductionEnabled ? api.get('/api/ai-production?status=Approved') : Promise.resolve({ data: [] }),
        api.get('/api/clients'),
      ]);
      setSlots(slotsRes.data);
      setApproved([
        ...liveRes.data.map(item => ({ ...item, track: 'Live' })),
        ...aiRes.data.map(item => ({ ...item, track: 'AI' })),
      ]);
      setClients(clientsRes.data);
    } catch {
      setError('The posting queue did not load. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [aiProductionEnabled]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Derived ──────────────────────────────────────────────────────────────────

  // Group slots by clientId, then by track, each track by slotOrder
  const slotsByClient = useMemo(() => {
    const map = {};
    slots.forEach(slot => {
      if (!map[slot.clientId]) map[slot.clientId] = { Live: [], AI: [] };
      const track = slot.track === 'AI' ? 'AI' : 'Live';
      map[slot.clientId][track].push(slot);
    });
    Object.values(map).forEach(byTrack => {
      byTrack.Live.sort((a, b) => a.slotOrder - b.slotOrder);
      byTrack.AI.sort((a, b) => a.slotOrder - b.slotOrder);
    });
    return map;
  }, [slots]);

  // Approved items not yet in queue, per clientId + track
  const availableByClientTrack = useMemo(() => {
    const inQueue = new Set();
    slots.forEach(s => {
      if (s.liveProductionId) inQueue.add(`live-${s.liveProductionId}`);
      if (s.aiProductionId)   inQueue.add(`ai-${s.aiProductionId}`);
    });
    const map = {};
    approved.forEach(item => {
      const key = `${item.clientId}-${item.track}`;
      if (!map[key]) map[key] = [];
      const qKey = item.track === 'Live' ? `live-${item.id}` : `ai-${item.id}`;
      if (!inQueue.has(qKey)) map[key].push(item);
    });
    return map;
  }, [slots, approved]);

  // Clients to show: those with slots OR with available approved items
  const visibleClientIds = useMemo(() => {
    const ids = new Set();
    slots.forEach(s => ids.add(s.clientId));
    approved.forEach(a => ids.add(a.clientId));
    return [...ids];
  }, [slots, approved]);

  const filteredClientIds = useMemo(() => {
    if (clientFilter) return visibleClientIds.filter(id => id === clientFilter);
    return visibleClientIds;
  }, [visibleClientIds, clientFilter]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  async function handleDateChange(slot, newDate) {
    const prev = slots;
    setSlots(s => s.map(sl => sl.id === slot.id ? { ...sl, scheduledDate: newDate || null } : sl));
    try {
      await api.patch(`/api/scheduler/${slot.id}`, { scheduledDate: newDate || null });
    } catch {
      setSlots(prev);
      toast('The date did not save. Try again.', { tone: 'danger' });
    }
  }

  async function handleMove(slot, trackSlots, dir) {
    const idx = trackSlots.findIndex(s => s.id === slot.id);
    const other = trackSlots[idx + dir];
    if (!other) return;
    const prevSlots = slots;
    setSlots(s => s.map(sl => {
      if (sl.id === slot.id)  return { ...sl, slotOrder: other.slotOrder };
      if (sl.id === other.id) return { ...sl, slotOrder: slot.slotOrder };
      return sl;
    }));
    try {
      await api.post('/api/scheduler/swap', { slotAId: slot.id, slotBId: other.id });
    } catch {
      setSlots(prevSlots);
      toast('The order did not save. Try again.', { tone: 'danger' });
    }
  }

  async function handleRemove(slot) {
    const name = prodOf(slot)?.script?.scriptName;
    const ok = await confirm({
      title: 'Remove from queue?',
      body: `Remove ${name ? clean(name) : 'this slot'} from the posting queue? The production stays in the pipeline; only the queue slot is removed.`,
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    const prev = slots;
    setSlots(s => s.filter(sl => sl.id !== slot.id));
    try {
      await api.delete(`/api/scheduler/${slot.id}`);
      const res = await api.get('/api/scheduler');
      setSlots(res.data);
    } catch {
      setSlots(prev);
      toast('The slot was not removed. Try again.', { tone: 'danger' });
    }
  }

  async function handleAddItem(clientId, track, item) {
    try {
      const body = { clientId, track };
      if (track === 'Live') body.liveProductionId = item.id;
      else                  body.aiProductionId   = item.id;
      const res = await api.post('/api/scheduler', body);
      setSlots(prev => [...prev, res.data]);
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The video was not added to the queue. Try again.', { tone: 'danger' });
    }
  }

  // ── Header ───────────────────────────────────────────────────────────────────

  const totalQueued = slots.length;
  usePageHeader({
    title: 'Posting scheduler',
    meta: loading ? 'Loading' : `${plural(totalQueued, 'video')} in queue`,
    actions: <Button variant="quiet" icon={RefreshCw} onClick={fetchAll}>Refresh</Button>,
    filters: (
      <>
        <Select aria-label="Project" value={clientFilter} onChange={e => setClientFilter(e.target.value)}
          options={[{ value: '', label: 'All projects' }, ...clients.map(c => ({ value: c.id, label: clean(c.name) }))]} />
        {clientFilter && <Button variant="quiet" icon={X} onClick={() => setClientFilter('')}>Clear</Button>}
      </>
    ),
  });

  const queueClients = filteredClientIds.map(clientId => {
    const cs = slotsByClient[clientId] ?? { Live: [], AI: [] };
    const liveAvail = availableByClientTrack[`${clientId}-Live`] ?? [];
    const aiAvail   = availableByClientTrack[`${clientId}-AI`]   ?? [];
    return { clientId, cs, liveAvail, aiAvail, hasLive: cs.Live.length > 0 || liveAvail.length > 0, hasAI: cs.AI.length > 0 || aiAvail.length > 0 };
  }).filter(c => c.hasLive || c.hasAI);

  return (
    <div className="ui ui-page">
      <Tabs label="Scheduler view" value={view} onChange={setView}
        items={[{ id: 'queue', label: 'Queue' }, { id: 'calendar', label: 'Calendar' }]} />

      {error && <ErrorState title="Scheduler did not load" message={error} onRetry={fetchAll} />}

      {!error && loading && (
        <div className="ui-region ui-region--pad ui-stack"><Skeleton width="25%" /><Skeleton width="60%" /><Skeleton width="50%" /><Skeleton width="55%" /></div>
      )}

      {!error && !loading && view === 'calendar' && (
        <SchedulerCalendar slots={slots} clients={clients} clientFilter={clientFilter} onDateChange={handleDateChange} />
      )}

      {!error && !loading && view === 'queue' && (queueClients.length === 0
        ? <div className="ui-region"><EmptyState message={clientFilter
            ? 'This project has no queued or approved videos. Choose All projects, or approve a cut in Qual first.'
            : 'No project has queued or approved videos. Approve a cut in Qual and it appears here.'} /></div>
        : queueClients.map(({ clientId, cs, liveAvail, aiAvail, hasLive, hasAI }) => {
          const clientObj = clients.find(c => c.id === clientId);
          const name      = clean(clientObj?.name ?? 'Unknown project');
          const total     = cs.Live.length + cs.AI.length;
          return (
            <section key={clientId} className="ui-section" aria-label={name}>
              <div className="ui-section__head">
                <h2 className="ui-section__title">{name}</h2>
                <span className="ui-meta">{plural(total, 'video')} queued</span>
              </div>
              <div className={hasLive && hasAI ? 'ui-cols' : 'ui-stack'}>
                {hasLive && (
                  <TrackColumn label="Live" slots={cs.Live} available={liveAvail} onDateChange={handleDateChange}
                    onMove={(s, dir) => handleMove(s, cs.Live, dir)} onRemove={handleRemove}
                    onAdd={item => handleAddItem(clientId, 'Live', item)} />
                )}
                {hasAI && (
                  <TrackColumn label="AI" slots={cs.AI} available={aiAvail} onDateChange={handleDateChange}
                    onMove={(s, dir) => handleMove(s, cs.AI, dir)} onRemove={handleRemove}
                    onAdd={item => handleAddItem(clientId, 'AI', item)} />
                )}
              </div>
            </section>
          );
        }))}
    </div>
  );
}

// ── Calendar ──────────────────────────────────────────────────────────────────

// A draggable chip for a slot. Click opens the peek (title, project, date, Remove date); × clears its date.
function SlotChip({ slot, clients, peekId, setPeekId, onDateChange, removable, align = 'start' }) {
  const title  = slotTitle(slot);
  const client = clients.find(c => c.id === slot.clientId);
  const open   = peekId === slot.id;
  return (
    <span className="ui-chip" draggable onDragStart={e => { e.stopPropagation(); e.dataTransfer.setData('text/plain', slot.id); }}
      title={`${title}${client ? `, ${clean(client.name)}` : ''}`}>
      <Popover open={open} onOpenChange={o => setPeekId(o ? slot.id : null)} label={title} align={align}
        trigger={({ toggle, ref, props }) => (
          <button type="button" ref={ref} className="ui-chip__main" onClick={toggle} {...props}>
            <span className="ui-chip__tag">{slot.track}</span>
            <span className="ui-chip__title">{title}</span>
          </button>
        )}>
        <div className="ui-popbody ui-popbody--narrow">
          <span><Tag>{slot.track}</Tag></span>
          <h3 className="ui-heading">{title}</h3>
          <p className="ui-p ui-dense ui-text-2">{client ? `${clean(client.name)}, ` : ''}{slot.scheduledDate ? formatDisplayDate(toInputDate(slot.scheduledDate)) : 'Unscheduled'}</p>
          {slot.scheduledDate && (
            <Button onClick={() => { onDateChange(slot, null); setPeekId(null); }}>Remove date</Button>
          )}
        </div>
      </Popover>
      {removable && (
        <button type="button" className="ui-chip__x" aria-label={`Remove ${title} from this date`} title="Remove from this date"
          onClick={() => onDateChange(slot, null)}>
          <Icon icon={X} />
        </button>
      )}
    </span>
  );
}

// The day's "schedule a video" picker: search the unscheduled slots, pick one to put it on this day.
function DayPicker({ date, align, unscheduled, clients, openDay, setOpenDay, onDateChange }) {
  const [search, setSearch] = useState('');
  const open = openDay === date;
  const q = search.trim().toLowerCase();
  const matches = !q ? unscheduled : unscheduled.filter(s => {
    const title = prodOf(s)?.script?.scriptName ?? '';
    const client = clients.find(c => c.id === s.clientId);
    return title.toLowerCase().includes(q) ||
           (client?.name || '').toLowerCase().includes(q) ||
           (client?.initials || '').toLowerCase().includes(q);
  });
  const dayLabel = formatDisplayDate(date);
  return (
    <Popover open={open} onOpenChange={o => { setOpenDay(o ? date : null); setSearch(''); }} align={align} label={`Schedule on ${dayLabel}`}
      trigger={({ toggle, ref, props }) => (
        <Button ref={ref} variant="quiet" iconOnly icon={Plus} label={`Schedule a video on ${dayLabel}`} onClick={toggle} {...props} />
      )}>
      <div className="ui-popbody">
        <span className="ui-field__label">Schedule on {dayLabel}</span>
        <Input aria-label="Search by title or project" placeholder="Search by title or project" value={search}
          onChange={e => setSearch(e.target.value)} autoFocus />
        <div className="ui-scroll ui-list">
          {matches.length === 0
            ? <p className="ui-p ui-dense ui-text-3">{unscheduled.length === 0 ? 'Every queued video already has a date.' : 'No video matches that search.'}</p>
            : matches.map(slot => (
              <ListRow key={slot.id} leading={<Tag>{slot.track}</Tag>} title={slotTitle(slot)}
                meta={clean(clients.find(c => c.id === slot.clientId)?.name)}
                onClick={() => { onDateChange(slot, date); setOpenDay(null); }} />
            ))}
        </div>
      </div>
    </Popover>
  );
}

function SchedulerCalendar({ slots, clients, clientFilter, onDateChange }) {
  const [viewDate, setViewDate] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [openDay,  setOpenDay]  = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [peekId,   setPeekId]   = useState(null);

  const today = new Date().toISOString().slice(0, 10);

  const filteredSlots = useMemo(() => (clientFilter ? slots.filter(s => s.clientId === clientFilter) : slots), [slots, clientFilter]);

  const slotsByDate = useMemo(() => {
    const map = {};
    filteredSlots.forEach(slot => {
      if (!slot.scheduledDate) return;
      const dateStr = toInputDate(slot.scheduledDate);
      if (!map[dateStr]) map[dateStr] = [];
      map[dateStr].push(slot);
    });
    return map;
  }, [filteredSlots]);

  const unscheduled = useMemo(() => filteredSlots.filter(s => !s.scheduledDate), [filteredSlots]);

  const year  = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const monthLabel = viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Six-or-fewer weeks, Sunday first; days outside the month are shown muted and take no items or drops (as before).
  const cells = useMemo(() => {
    const firstDow = new Date(year, month, 1).getDay();
    const daysInMon = new Date(year, month + 1, 0).getDate();
    const count = Math.ceil((firstDow + daysInMon) / 7) * 7;
    return Array.from({ length: count }, (_, i) => {
      const d = new Date(year, month, 1 - firstDow + i);
      return { iso: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, inMonth: d.getMonth() === month, dow: i % 7 };
    });
  }, [year, month]);

  const drop = dateStr => e => {
    e.preventDefault(); setDragOver(null);
    const id = e.dataTransfer.getData('text/plain');
    if (id) {
      const slot = slots.find(s => s.id === id);
      if (slot) onDateChange(slot, dateStr);
    }
  };

  return (
    <div className="ui-stack">
      <div className="ui-toolbar">
        <Button variant="quiet" iconOnly icon={ChevronLeft} label="Previous month" onClick={() => setViewDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))} />
        <span className="ui-heading" aria-live="polite">{monthLabel}</span>
        <Button variant="quiet" iconOnly icon={ChevronRight} label="Next month" onClick={() => setViewDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))} />
        <span className="ui-spacer" />
        <span className="ui-meta">Drag a video onto a day to set its date.</span>
      </div>

      <div className="ui-month">
        {DOW.map(d => <div key={d} className="ui-month__dow" aria-hidden="true">{d}</div>)}
        {cells.map(({ iso, inMonth, dow }) => {
          if (!inMonth) return <CalendarCell key={iso} date={iso} outside />;
          const daySlots = slotsByDate[iso] || [];
          return (
            <CalendarCell key={iso} date={iso} today={iso === today} max={CAL_MAX_CHIPS}
              items={daySlots} renderItem={slot => <SlotChip slot={slot} clients={clients} peekId={peekId} setPeekId={setPeekId} onDateChange={onDateChange} removable align={dow >= 4 ? 'end' : 'start'} />}
              action={<DayPicker date={iso} align={dow >= 4 ? 'end' : 'start'} unscheduled={unscheduled} clients={clients}
                openDay={openDay} setOpenDay={setOpenDay} onDateChange={onDateChange} />}
              data-drop={dragOver === iso || undefined}
              onDragOver={e => { e.preventDefault(); setDragOver(iso); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={drop(iso)} />
          );
        })}
      </div>

      {unscheduled.length > 0 && (
        <section className="ui-section" aria-labelledby="sched-unscheduled">
          <div className="ui-section__head">
            <h2 className="ui-section__title" id="sched-unscheduled">Unscheduled</h2>
            <span className="ui-meta">{unscheduled.length}</span>
          </div>
          <div className="ui-chip-row">
            {unscheduled.map(slot => (
              <SlotChip key={slot.id} slot={slot} clients={clients} peekId={peekId} setPeekId={setPeekId} onDateChange={onDateChange} removable={false} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ── Queue ─────────────────────────────────────────────────────────────────────

function TrackColumn({ label, slots, available, onDateChange, onMove, onRemove, onAdd }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [adding,   setAdding]   = useState(false);

  async function handleAdd(item) {
    setAdding(true);
    setMenuOpen(false);
    try {
      await onAdd(item);
    } finally {
      setAdding(false);
    }
  }

  // D-120: episode candidates nest under a series label that cannot be picked.
  const menuItems = groupEpisodes(available).flatMap(entry => (entry.kind === 'series'
    ? [
        { label: `${clean(entry.scriptName)} (${plural(entry.episodes.length, 'episode')})`, disabled: true },
        ...entry.episodes.map(item => ({ label: episodeLabel(item), onSelect: () => handleAdd(item) })),
      ]
    : [{ label: clean(entry.record.script?.scriptName ?? 'No script'), onSelect: () => handleAdd(entry.record) }]));

  return (
    <div className="ui-region">
      <div className="ui-row">
        <Tag>{label}</Tag>
        <span className="ui-meta">{plural(slots.length, 'slot')}</span>
      </div>

      {slots.length === 0
        ? <ListRow title={<span className="ui-text-3">No videos queued. Add an approved video below.</span>} />
        : (
          <div className="ui-list">
            {slots.flatMap((slot, idx) => {
              const production = prodOf(slot);
              const episode    = isEpisodeCard(production);
              const seriesId   = episode ? (production.scriptId || production.script?.id) : null;
              // D-120: episode slots are titled "Ep N: <title>" and sit under ONE series label, emitted before the
              // FIRST slot of that series. Slot order is untouched.
              const title      = episode ? episodeLabel(production) : clean(production?.script?.scriptName ?? `Slot #${slot.slotOrder}`);
              const sameSeries = s => { const p = prodOf(s); return isEpisodeCard(p) && (p.scriptId || p.script?.id) === seriesId; };
              const firstOfSeries = episode && !slots.slice(0, idx).some(sameSeries);
              const seriesCount   = firstOfSeries ? slots.filter(sameSeries).length : 0;
              const row = (
                <SlotRow key={slot.id} num={idx + 1} slot={slot} title={title} nested={episode}
                  isFirst={idx === 0} isLast={idx === slots.length - 1}
                  onDateChange={onDateChange} onMove={onMove} onRemove={onRemove} />
              );
              return firstOfSeries
                ? [
                    <div key={`series:${seriesId}`} data-testid="sched-series">
                      <ListRow title={<span className="ui-text-2">{clean(production.script?.scriptName || 'Series')} ({plural(seriesCount, 'episode')})</span>} />
                    </div>,
                    row,
                  ]
                : [row];
            })}
          </div>
        )}

      <div className="ui-row">
        <Popover open={menuOpen} onOpenChange={setMenuOpen} label={`Add a ${label} video`}
          trigger={({ toggle, ref, props }) => (
            <Button ref={ref} variant="quiet" icon={Plus} loading={adding} disabled={available.length === 0}
              title={available.length === 0 ? 'No approved videos available for this track' : 'Add a video to the queue'}
              onClick={toggle} {...props}>
              {adding ? 'Adding' : available.length === 0 ? 'No videos available' : 'Add video'}
            </Button>
          )}>
          <div className="ui-scroll" data-testid="sched-add-menu">
            <Menu label={`Approved ${label} videos`} items={menuItems} onClose={() => setMenuOpen(false)} />
          </div>
        </Popover>
      </div>
    </div>
  );
}

function SlotRow({ num, slot, title, nested = false, isFirst, isLast, onDateChange, onMove, onRemove }) {
  const [dateValue, setDateValue] = useState(toInputDate(slot.scheduledDate));

  // Keep the local date in sync if the slot changes from outside (calendar, rollback)
  useEffect(() => { setDateValue(toInputDate(slot.scheduledDate)); }, [slot.scheduledDate]);

  // Commit on blur, Enter or Escape, as before (typing a date does not save each keystroke).
  const commit = () => { if (dateValue !== toInputDate(slot.scheduledDate)) onDateChange(slot, dateValue); };

  return (
    <ListRow
      leading={<>{nested && <span className="ui-indent" aria-hidden="true" />}<span className="ui-meta ui-num">#{num}</span></>}
      title={<span title={title}>{title}</span>}
      trailing={(
        <>
          <DatePicker aria-label={`Post date for ${title}`} value={dateValue} onChange={e => setDateValue(e.target.value)}
            onBlur={commit} onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') e.target.blur(); }} />
          <Button variant="quiet" iconOnly icon={ArrowUp} label="Move up" disabled={isFirst} onClick={() => onMove(slot, -1)} />
          <Button variant="quiet" iconOnly icon={ArrowDown} label="Move down" disabled={isLast} onClick={() => onMove(slot, 1)} />
          <Button variant="quiet" iconOnly icon={X} label="Remove from queue" onClick={() => onRemove(slot)} />
        </>
      )}
    />
  );
}
